/**
 * Console-side funnel seams for an accepted invitation.
 *
 * Each is called at the moment its name describes; nothing here knows about an
 * analytics vendor. A console analytics client registers a reporter once at
 * startup and these become real events — until then they are inert, so a
 * landing surface never has to care whether one is installed.
 *
 * The set is deliberately smaller than it was. The desktop interstitial these
 * originally measured is gone, so `invite_handoff_shown` (the card rendered)
 * and `invite_browser_instead_chosen` (the escape hatch was taken) described
 * surfaces that no longer exist. `invite_first_open` replaces them and is the
 * one that matters now: a browser landing is only worth anything if the new
 * teammate opens something in it.
 */

import type { AuthErrorReason } from './authErrorMessage';
import type { FirstOpenKind, OfferPlacement } from '../analytics/invitationAnalyticsContract';

export type InvitationHandoffEvent =
  | 'invite_landing_viewed'
  | 'invite_landing_failed'
  | 'invite_deep_link_followed'
  | 'invite_download_clicked'
  | 'invite_first_open';

export type InvitationHandoffReporter = (
  event: InvitationHandoffEvent,
  properties: Record<string, unknown>,
) => void;

let reporter: InvitationHandoffReporter | null = null;

/** Install (or with `null`, remove) the reporter these seams emit through. */
export function setInvitationHandoffReporter(next: InvitationHandoffReporter | null): void {
  reporter = next;
}

function report(event: InvitationHandoffEvent, properties: Record<string, unknown>): void {
  try {
    reporter?.(event, properties);
  } catch (cause) {
    // Reporting is never allowed to break the invitee's landing.
    console.warn('[auth] invitation handoff reporter failed', cause);
  }
}

/** The invitation callback route rendered. This is deliberately pre-auth safe. */
export function invitationLandingViewed(): void {
  report('invite_landing_viewed', {});
}

/**
 * The callback could not complete the invitation.
 *
 * `recovered` is the half that matters: the route now looks for a live session
 * before it renders an error, so a failure the invitee never saw is a different
 * event from one they did. Without this the only trace of NIM-5382 in
 * production was an `invite_landing_viewed` with nothing after it, which is why
 * diagnosing it meant reasoning about events that were absent.
 */
export function invitationLandingFailed(
  detail: { reason: AuthErrorReason; recovered: boolean },
): void {
  report('invite_landing_failed', detail);
}

/**
 * The invitee chose to open the desktop app. Always a deliberate click now —
 * nothing navigates to `nimbalyst://` on their behalf, which is why the old
 * `auto` flag is gone rather than pinned to false.
 */
export function invitationDeepLinkFollowed(detail: { placement: OfferPlacement }): void {
  report('invite_deep_link_followed', detail);
}

export function invitationDownloadClicked(detail: { placement: OfferPlacement }): void {
  report('invite_download_clicked', detail);
}

/**
 * The invitee opened something real for the first time. Fired once per browser
 * session by the console shell, not once per open.
 */
export function invitationFirstOpen(detail: { kind: FirstOpenKind }): void {
  report('invite_first_open', detail);
}
